import React from 'react';
import tw from 'twin.macro';

import { isValidURL } from '$/utils/isUrl';
import { EditableInput, EditableInputProps } from '../../EditableInput';
import { css } from '@emotion/react';
import { Input } from 'semantic-ui-react';
import { markdownToText } from '$/shared/frontend/utils/MarkdownConverter';

type UrlInputProps = Omit<
  EditableInputProps,
  'onHandleEnter' | 'showInlineToolbar' | 'showSideToolbar' | 'showStaticToolbar' | 'showFocusBox'
>;

const UrlInput: React.FC<React.PropsWithChildren<UrlInputProps>> = ({
  children,
  className,
  value,
  onChange,
  ...props
}) => {
  const handleEnter = React.useCallback(
    (v: string | null | undefined): void => {
      const valueTrim = markdownToText(v ?? value ?? '').trim();
      if (!valueTrim || !isValidURL(valueTrim)) {
        return;
      }

      let newUrl = valueTrim;
      if (!valueTrim.startsWith('http')) {
        newUrl = 'https://' + valueTrim;
      }
      onChange && onChange(newUrl);
    },
    [onChange, value]
  );

  return props.mentions ? (
    <EditableInput
      withBorder
      noLinkify
      showInlineToolbar={false}
      showSideToolbar={false}
      showStaticToolbar={false}
      showFocusBox={false}
      noFormatting
      isUrlInput
      placeholder='Enter URL or type { to choose merge field'
      className={className}
      css={css`
        ${tw`text-color-input-field bg-white`}
        &::placeholder {
          opacity: 1;
          ${tw`text-color-input-placeholder`}
        }
        &:disabled {
          opacity: 0.4;
        }
      `}
      {...props}
      value={value ?? ''}
      onHandleEnter={(value: string | null): boolean => {
        handleEnter(value);
        return true;
      }}
      onBlur={(_e, value): void => {
        return handleEnter(value);
      }}
      onChange={onChange}
    >
      {children}
    </EditableInput>
  ) : (
    <Input
      className={className}
      css={css`
        &::placeholder {
          opacity: 1;
          ${tw`text-color-input-placeholder`}
        }
        &:disabled {
          opacity: 0.4;
        }
      `}
      {...props}
      value={value ?? ''}
      onBlur={(e: React.FocusEvent<HTMLInputElement>): void => handleEnter(e.target.value)}
      onKeyDown={(e: KeyboardEvent): void => {
        if (e.keyCode === 13) {
          handleEnter(value);
        }
      }}
      onChange={(_e, data): void => {
        onChange && onChange(data.value);
      }}
    >
      {children}
    </Input>
  );
};

export default UrlInput;
